import { SystemConfig, createSystem, defaultConfig } from "@chakra-ui/react"
import { useMemo } from "react"
import {
  UnderscoredColorPalette,
  getColorPaletteSemanticTokens,
  getPrimaryPalette
} from "../../utils/color"
import { systemConfig } from "./themes/platform/theme"
import { Provider } from "./ui/provider"

const defaultBrandColorPalette: UnderscoredColorPalette = {
  _50: "#f5f9ff",
  _100: "#e0edfe",
  _200: "#b7d5fe",
  _300: "#8ebcfd",
  _400: "#63a4fc",
  _500: "#388afc",
  _600: "#0d71fb",
  _700: "#045dd8",
  _800: "#034aab",
  _900: "#02367e",
  _950: "#022c67"
}

export type ChakraProviderProps = {
  children: React.ReactNode
  brandColorPalette?: UnderscoredColorPalette | null
  systemConfig?: SystemConfig | null
  resetCSS?: boolean // to support current provider prop usage
}

export const ChakraProvider = ({
  children,
  brandColorPalette,
  systemConfig: systemConfigProp,
  resetCSS
}: ChakraProviderProps) => {
  const systemValue = useMemo(() => {
    const systemConfigs: SystemConfig[] = [
      {
        ...systemConfig,
        preflight: resetCSS || false
      }
    ]

    // Add custom system config if provided.
    if (systemConfigProp) {
      systemConfigs.push(systemConfigProp)
    }

    brandColorPalette ||= defaultBrandColorPalette

    // Add brand colors if provided.
    if (brandColorPalette) {
      const brand = getPrimaryPalette(brandColorPalette)
      systemConfigs.push({
        theme: {
          tokens: {
            colors: {
              brand
            }
          },
          semanticTokens: {
            colors: {
              // Supporting current primaryBrandColor token, but wouldn't
              // directly using brand.600 be fine?
              primaryBrandColor: {
                main: { value: "brand.600" }
              },
              // The "brand" semantic tokens are necessary for colorPalette="brand" usage.
              ...getColorPaletteSemanticTokens("brand"),
              // The black/white alpha palettes existed in Chakra UI v2 but not v3,
              // so we add their semantic tokens here for backward compatibility.
              // Note that in Chakra v2 the main color value mapped to 500 while
              // in Chakra v3 it maps to 600, but, at least in the case of blackAlpha
              // and whiteAlpha, the values did not get remapped, so to maintain backward
              // compatibility, we need to specify the value that the main color value maps to,
              // which in those cases is 500.
              ...getColorPaletteSemanticTokens("blackAlpha", {
                primaryValue: "500",
                contrastColor: "white"
              }),
              ...getColorPaletteSemanticTokens("whiteAlpha", {
                primaryValue: "500",
                contrastColor: "white"
              })
            }
          }
        }
      })
    }

    return createSystem(defaultConfig, ...systemConfigs)
  }, [systemConfigProp, brandColorPalette, resetCSS])

  return <Provider value={systemValue}>{children}</Provider>
}

export default ChakraProvider
